import type { Structure, LayoutData, LayoutRefs, SingleLayoutData } from '@wix/thunderbolt-becky-types'
import type { CompsInFoldMap } from '../types'
import type { BfsPredicate } from './getComponentDescendants'
import { getComponentDescendantsBfs, getChildrenCb } from './getComponentDescendants'
import { getClassicLayout, removeHash } from '../functionLibrary'
import _ from 'lodash'

// This number is determined by taking the 80th percentile out of all heights in this link: https://experienceleague.adobe.com/docs/target/using/experiences/vec/mobile-viewports.html?lang=en
export const HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE = 731
//  This number is determined by taking the most common desktop height as specified in this link: https://www.browserstack.com/guide/ideal-screen-sizes-for-responsive-design
export const HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP = 1080

export const compsInFoldTypesList = ['WPhoto', 'ImageX']

type PagesObject = { nonPopupPages: Array<string>; popupPages: Array<string> }

const getAllPages = (structure: Structure): PagesObject => {
	const allPages = Object.keys(structure).filter((compId) => structure[compId].type === 'Page')
	return allPages.reduce<PagesObject>(
		(acc, pageId) => {
			const children = structure[pageId].components
			const isPopupPage =
				children && children.length > 0 && structure[children[0]]?.componentType.endsWith('PopupContainer')
			acc[isPopupPage ? 'popupPages' : 'nonPopupPages'].push(pageId)
			return acc
		},
		{ nonPopupPages: [], popupPages: [] }
	)
}

export const getCompsInFirstFold = (structure: Structure, threshold: number): CompsInFoldMap => {
	const { nonPopupPages, popupPages } = getAllPages(structure)
	const pagesContainerY = structure.PAGES_CONTAINER?.layout.y || 0

	const pagesAbsoluteY = nonPopupPages.reduce(
		(acc, pageId) => Object.assign(acc, { [pageId]: (structure[pageId].layout.y || 0) + pagesContainerY }),
		{}
	)
	const popupPagesAbsoluteY = popupPages.reduce(
		(acc, popupPageId) => Object.assign(acc, { [popupPageId]: structure[popupPageId].layout.y || 0 }),
		{}
	)

	const compsAbsoluteY: Record<string, number> = {
		...pagesAbsoluteY,
		...popupPagesAbsoluteY,
		masterPage: structure.masterPage?.layout.y || 0,
	}
	const fixedPositionComps: Record<string, boolean> = {}
	const isInFirstFoldPredicate: BfsPredicate = (nodeId) => {
		if ([...nonPopupPages, ...popupPages, 'masterPage'].includes(nodeId)) {
			return compsAbsoluteY[nodeId] < threshold
		}
		const parentId: string | null | undefined = structure[nodeId!]?.parent
		const isParentFixedPosition =
			parentId && (structure[parentId].layout.fixedPosition || fixedPositionComps[parentId])
		const isNodeFixedPosition = structure[nodeId]?.layout.fixedPosition
		if (isNodeFixedPosition || isParentFixedPosition) {
			fixedPositionComps[nodeId] = true
		}
		compsAbsoluteY[nodeId] =
			(parentId && !isParentFixedPosition ? compsAbsoluteY[parentId] : 0) + (structure[nodeId]?.layout.y || 0)
		return compsAbsoluteY[nodeId] < threshold
	}

	return getComponentDescendantsBfs({
		roots: [...nonPopupPages, ...popupPages, 'masterPage'],
		predicate: isInFirstFoldPredicate,
		getChildren: getChildrenCb(structure),
		shouldStopUponFalsePredicate: true,
	})
}

export const getCompsOfFirstLevelContainersInFold = (
	structure: Structure,
	layoutData: LayoutData,
	isMobileView: boolean,
	threshold: number
): CompsInFoldMap => {
	const getClassicLayoutOfComp = (compId: string): SingleLayoutData | undefined => {
		const layoutQuery = structure[compId]?.layoutQuery || ''
		const layoutRefArray = layoutData[layoutQuery] as LayoutRefs
		const layoutItems = layoutRefArray?.values.map((layoutRef) => layoutData[removeHash(layoutRef) || ''])
		return getClassicLayout(layoutItems, isMobileView, true)
	}

	const isFixedPosition = (compId: string): boolean => {
		const classicLayout = getClassicLayoutOfComp(compId)
		return (
			classicLayout?.itemLayout?.type === 'FixedPositionItemLayout' ||
			classicLayout?.itemLayout?.type === 'FixedItemLayout'
		)
	}

	const getCompTop = (compId: string): number => {
		const classicLayout = getClassicLayoutOfComp(compId)
		return classicLayout?.itemLayout?.type === 'MeshItemLayout' ? classicLayout.itemLayout.meshData.y : 0
	}

	const { nonPopupPages, popupPages } = getAllPages(structure)
	const pagesContainerLayout = getClassicLayoutOfComp('PAGES_CONTAINER')
	const pagesContainerTop =
		pagesContainerLayout?.itemLayout?.type === 'MasterPageItemLayout' &&
		pagesContainerLayout.itemLayout.top?.type === 'px'
			? pagesContainerLayout.itemLayout.top.value
			: 0

	const roots = _.flatten(
		[...nonPopupPages, ...popupPages, 'masterPage'].map((pageId) =>
			structure[pageId]?.components?.filter(
				(compId) =>
					isFixedPosition(compId) ||
					getCompTop(compId) + (nonPopupPages.includes(pageId) ? pagesContainerTop : 0) < threshold
			)
		)
	)

	return getComponentDescendantsBfs({
		roots: roots as Array<string>,
		predicate: () => true,
		getChildren: getChildrenCb(structure),
		shouldStopUponFalsePredicate: true,
	})
}
